// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { extractUserFacingMessage } from '@/api/errorMessage';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  ChatCheckpoint,
  ChatReplayResponse,
  Message,
  Session,
  ClassContext,
  ThreadTask,
} from '@/types';
import { api } from '@api/client';
import { resolveRetryDelayMs } from '@api/retryPolicy';
import { buildChatStorePersistedSlice, hasLikelyStaleSessionCache } from './chatStore.cache';
import { createSessionListOrchestrator } from './chatStore.sessionListOrchestrator';
import { createSyncRuntimeMessagesAction } from './chatStore.syncProjection';
import { createThreadReplaySlice } from './chatStore.threadReplay';
import { createUiActionSlice } from './chatStore.uiAction';
import {
  expireConversationVideos,
  extractVideoFileIds,
  getSessionMessagesForVideoExpiration,
} from './chatStore.videoExpiration';
import {
  applyAuthReconciled,
  applySessionArchived,
  applySessionCreated,
  applySessionDeleted,
  applySessionRenamed,
  applySessionUnarchived,
  buildDraftSessionState,
  computeSessionSelectionState,
  DEFAULT_DRAFT_ACTOR_MODE,
  sanitizeRehydratedStateForUser,
  shouldRebuildThreadViewOnRehydrate,
} from './chatStore.session';
import {
  type ThreadRuntimeView,
  applyThreadSwitchToThreadView,
  appendLocalMessageToThreadView,
  createEmptyThreadView,
} from './chatStore.threadView';

// 会话列表翻页失败的退避重试：同一时刻只挂一个定时器；退避窗口内滚动触发的
// 补页调用直接吸收，避免对着正在 429 的端点连发。
let loadMoreRetryTimer: ReturnType<typeof setTimeout> | null = null;
let loadMoreRetryNotBefore = 0;

function scheduleLoadMoreRetry(fn: () => void, delayMs: number): void {
  if (loadMoreRetryTimer) clearTimeout(loadMoreRetryTimer);
  loadMoreRetryNotBefore = Date.now() + delayMs;
  loadMoreRetryTimer = setTimeout(() => {
    loadMoreRetryTimer = null;
    fn();
  }, delayMs);
}

function isLoadMoreInBackoff(): boolean {
  return Date.now() < loadMoreRetryNotBefore;
}

function deriveSessionTitleFromPrompt(prompt: string): string | undefined {
  const normalizedPrompt = prompt.trim();
  if (!normalizedPrompt) {
    return undefined;
  }
  return normalizedPrompt.slice(0, 50);
}

function normalizeActorMode(raw: unknown): Session['actor_mode'] | undefined {
  return raw === 'teacher' || raw === 'student' ? raw : undefined;
}

export interface ChatState {
  currentSession: Session | null;
  currentThreadId: string | null;
  draftActorMode: 'teacher' | 'student';
  classContext: ClassContext | null;
  messages: Message[];
  historicalMessageIds: string[];
  currentThreadView: ThreadRuntimeView | null;
  checkpoints: ChatCheckpoint[];
  checkpointsLoading: boolean;
  replayLoading: boolean;
  lastReplayCheckpointId: string | null;
  currentThreadTasks: ThreadTask[];
  messagesBySession: Record<string, Message[]>;
  threadTasksByThread: Record<string, ThreadTask[]>;
  missingHistoryBySession: Record<string, true>;
  sessions: Session[];
  sessionsLoadedAt: number;
  isLoadingSessions: boolean;
  sessionsTotal: number;
  sessionsPage: number;
  chatError: string | null;
  backgroundError: string | null;
  showArchived: boolean;
  deepThinking: boolean | null;
  webSearch: boolean;
  setWebSearchEnabled: (enabled: boolean) => void;

  setCurrentSession: (session: Session | null) => Promise<void>;
  loadThreadHistory: (threadId: string, sessionId: string) => Promise<void>;
  loadThreadTasks: (threadId: string) => Promise<ThreadTask[]>;
  loadThreadCheckpoints: (threadId: string) => Promise<ChatCheckpoint[]>;
  replayCheckpoint: (
    threadId: string,
    checkpointId: string,
    sessionId?: string
  ) => Promise<ChatReplayResponse | null>;
  syncRuntimeMessages: (sessionId: string, messages: Message[]) => void;
  /**
   * Append a single locally-created message to the current session's message
   * mirrors (messages / messagesBySession / currentThreadView). Used by the
   * DeepAgent bridge to project a completed DeepAgent result and DeepAgent-owned
   * follow-up user turns into the main-chat transcript immediately. The message
   * is de-duplicated by id so repeated callbacks are idempotent. Does NOT touch
   * historicalMessageIds (the message is not from server history) and does NOT
   * run the main-chat agent graph.
   */
  appendLocalMessage: (sessionId: string, message: Message) => void;
  appendLocalAssistantMessage: (sessionId: string, message: Message) => void;
  setActorMode: (mode: 'teacher' | 'student') => void;
  switchActorMode: (mode: 'teacher' | 'student') => void;
  setClassContext: (ctx: ClassContext | null) => void;
  loadSessions: (options?: { force?: boolean }) => Promise<void>;
  loadMoreSessions: () => Promise<void>;
  reconcileAuthUser: (userId: string | null) => void;
  startDraftSession: () => void;
  createSession: (title?: string) => Promise<void>;
  createSessionFromPrompt: (prompt: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  renameSession: (sessionId: string, title: string) => Promise<void>;
  archiveSession: (sessionId: string) => Promise<void>;
  unarchiveSession: (sessionId: string) => Promise<void>;
  toggleShowArchived: () => void;
  shareSession: (sessionId: string) => Promise<string>;
  clearMessages: () => void;
  sendUiAction: (
    messageId: string,
    blockId: string,
    action: string,
    params?: Record<string, unknown>
  ) => Promise<Record<string, unknown> | undefined>;
  clearChatError: () => void;
  toggleDeepThinking: (currentValue: boolean) => void;
  toggleWebSearch: () => void;
}

type ThreadAuthoritySnapshot = {
  currentSession: Session | null;
  currentThreadId: string | null;
  currentThreadView?: ThreadRuntimeView | null;
};

type TranscriptAuthoritySnapshot = {
  currentThreadView: ThreadRuntimeView | null;
  messages: Message[];
  historicalMessageIds: string[];
};

function appendLocalMessagePatch(
  state: ChatState,
  sessionId: string,
  message: Message,
): ChatState {
  const isCurrentSession = state.currentSession?.session_id === sessionId;
  const cached = state.messagesBySession[sessionId] ?? [];
  const existingIndex = cached.findIndex((m) => m.id === message.id);
  const nextCached =
    existingIndex >= 0
      ? cached.map((item, index) => (index === existingIndex ? message : item))
      : [...cached, message];
  const nextMessagesBySession = {
    ...state.messagesBySession,
    [sessionId]: nextCached,
  };
  // Only the currently-active thread view + top-level messages mirror get the
  // appended message; other sessions' caches are updated but not projected
  // until their thread becomes active.
  let nextThreadView = state.currentThreadView;
  let nextMessages = state.messages;
  const threadId = isCurrentSession ? selectCurrentThreadId(state) : null;
  if (isCurrentSession && threadId && state.currentThreadView) {
    nextThreadView = appendLocalMessageToThreadView(state.currentThreadView, {
      threadId,
      message,
    });
    nextMessages = nextThreadView.messages;
  } else if (isCurrentSession) {
    nextMessages =
      existingIndex >= 0
        ? state.messages.map((item) => (item.id === message.id ? message : item))
        : [...state.messages, message];
  }
  return {
    ...state,
    messagesBySession: nextMessagesBySession,
    currentThreadView: nextThreadView,
    messages: nextMessages,
  };
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => {
      const sessionListOrchestrator = createSessionListOrchestrator({
        getState: get,
        setState: (patchOrUpdater) => {
          if (typeof patchOrUpdater === 'function') {
            set((state) => patchOrUpdater(state) as Partial<ChatState>);
            return;
          }
          set(patchOrUpdater as Partial<ChatState>);
        },
        fetchSessions: async () => {
          const { data } = await api.getSessions(1, 20);
          return {
            sessions: data.sessions || [],
            total: data.total || 0,
          };
        },
      });

      return {
        currentSession: null,
        currentThreadId: null,
        draftActorMode: DEFAULT_DRAFT_ACTOR_MODE,
        classContext: null,
        messages: [],
        historicalMessageIds: [],
        currentThreadView: null,
        checkpoints: [],
        checkpointsLoading: false,
        replayLoading: false,
        lastReplayCheckpointId: null,
        currentThreadTasks: [],
        messagesBySession: {},
        threadTasksByThread: {},
        missingHistoryBySession: {},
        sessions: [],
        sessionsLoadedAt: 0,
        isLoadingSessions: false,
        sessionsTotal: 0,
        sessionsPage: 1,
        chatError: null,
        backgroundError: null,
        showArchived: false,
        deepThinking: null,
        webSearch: false,
        setWebSearchEnabled: (enabled) => set({ webSearch: enabled }),

        setCurrentSession: async (session) => {
          const sessionId = session?.session_id ?? null;

          const rawCachedMessages = sessionId ? get().messagesBySession[sessionId] : undefined;
          const historyKnownMissing = Boolean(
            sessionId && get().missingHistoryBySession[sessionId]
          );
          const ignoreCachedMessages = hasLikelyStaleSessionCache(
            session ?? null,
            rawCachedMessages,
            historyKnownMissing
          );
          const cachedMessages = ignoreCachedMessages ? undefined : rawCachedMessages;
          const shouldReloadEmptyCache = Boolean(
            session &&
              cachedMessages &&
              cachedMessages.length === 0 &&
              !historyKnownMissing
          );

          // Switch or clear the thread runtime view
          const currentView = get().currentThreadView;
          const nextThreadView = session
            ? applyThreadSwitchToThreadView(
                currentView ?? createEmptyThreadView(session.thread_id),
                { threadId: session.thread_id, sessionId: session.session_id }
              )
            : null;

          set({
            ...computeSessionSelectionState(get(), session, { cachedMessages }),
            currentThreadView: nextThreadView
              ? { ...nextThreadView, messages: cachedMessages ?? [] }
              : null,
          });

          if (!session || (cachedMessages && !shouldReloadEmptyCache)) {
            return;
          }

          await Promise.all([
            get().loadThreadHistory(session.thread_id, session.session_id),
            get().loadThreadTasks(session.thread_id),
          ]);
        },

        loadThreadHistory: createThreadReplaySlice(set, get).loadThreadHistory,
        loadThreadTasks: createThreadReplaySlice(set, get).loadThreadTasks,
        loadThreadCheckpoints: createThreadReplaySlice(set, get).loadThreadCheckpoints,
        replayCheckpoint: createThreadReplaySlice(set, get).replayCheckpoint,

        syncRuntimeMessages: createSyncRuntimeMessagesAction(set),

        appendLocalMessage: (sessionId, message) => {
          set((state) => appendLocalMessagePatch(state, sessionId, message));
        },

        appendLocalAssistantMessage: (sessionId, message) => {
          set((state) => appendLocalMessagePatch(state, sessionId, message));
        },

        setActorMode: (mode) => set({ draftActorMode: mode }),

        // CI-H6: switching the actor role mid-conversation starts a fresh
        // draft session so teacher and student contexts never accumulate on
        // the same thread; the previous session stays in the sidebar list.
        switchActorMode: (mode) => {
          if (get().currentSession) {
            set({ ...buildDraftSessionState(mode), currentThreadView: null });
          } else {
            set({ draftActorMode: mode });
          }
        },

        setClassContext: (ctx) => set({ classContext: ctx }),

        sendUiAction: createUiActionSlice(set, get).sendUiAction,

        clearChatError: () => set({ chatError: null }),

        loadSessions: async (options) => {
          if (options?.force) sessionListOrchestrator.applyMutation(() => ({ sessionsLoadedAt: 0 }));
          set({ sessionsPage: 1 });
          await sessionListOrchestrator.load();
        },

        loadMoreSessions: async () => {
          const state = get();
          if (state.isLoadingSessions) return;
          if (isLoadMoreInBackoff()) return;
          const nextPage = state.sessionsPage + 1;
          set({ isLoadingSessions: true });
          try {
            const { data } = await api.getSessions(nextPage, 20);
            const newSessions = data.sessions || [];
            const total = data.total || 0;
            set((prev) => ({
              sessions: [...prev.sessions, ...newSessions],
              sessionsPage: nextPage,
              // 空页熔断：服务端 total 虚高（如归档会话计数）时钳制到已加载数，
              // 否则滚动触点永久武装、每次滚动都拉一页空结果。
              sessionsTotal: newSessions.length === 0 ? prev.sessions.length : total,
              isLoadingSessions: false,
            }));
          } catch (error) {
            set({ isLoadingSessions: false, backgroundError: extractUserFacingMessage(error) });
            // 失败退避自动补页（尊重 Retry-After，5–60s 夹取）：此前失败后
            // 只能靠用户继续滚动才重试，429 窗口内会话列表就永远停在半截。
            scheduleLoadMoreRetry(
              () => { void get().loadMoreSessions(); },
              resolveRetryDelayMs(error),
            );
          }
        },

        reconcileAuthUser: (userId) => {
          sessionListOrchestrator.applyMutation((state) =>
            applyAuthReconciled(state as ChatState, userId)
          );
          if (!userId) {
            set({ currentThreadView: null });
          }
        },

        startDraftSession: () => set({ ...buildDraftSessionState(), currentThreadView: null }),

        createSession: async (title) => {
          try {
            const draftMode = get().draftActorMode;
            const { data } = await api.createSession(title, { actor_mode: draftMode });
            const nextSession: Session = {
              ...data,
              actor_mode: normalizeActorMode((data as Session).actor_mode) ?? draftMode,
            };
            sessionListOrchestrator.applyMutation((state) =>
              applySessionCreated(state as ChatState, nextSession)
            );
            set({
              currentThreadView: {
                ...createEmptyThreadView(nextSession.thread_id),
                sessionId: nextSession.session_id,
              },
            });
          } catch (error) {
            set({ backgroundError: extractUserFacingMessage(error) });
            // H-8: propagate the failure so callers (e.g. handleSend's
            // createSessionFromPrompt path) can reset submission-in-flight
            // state. Swallowing here left the composer stuck on "submitting"
            // forever when session creation failed.
            throw error;
          }
        },

        createSessionFromPrompt: async (prompt) => {
          await get().createSession(deriveSessionTitleFromPrompt(prompt));
        },

        deleteSession: async (sessionId) => {
          try {
            await api.deleteSession(sessionId);
            const stateSnapshot = get();
            const sessionMessages = getSessionMessagesForVideoExpiration(stateSnapshot, sessionId);
            const videoFileIds = extractVideoFileIds(sessionMessages);
            if (videoFileIds.length > 0) {
              expireConversationVideos(sessionId, videoFileIds);
            }
            sessionListOrchestrator.applyMutation((state) =>
              applySessionDeleted(state as ChatState, sessionId)
            );
          } catch (error) {
            set({ backgroundError: extractUserFacingMessage(error) });
          }
        },

        renameSession: async (sessionId, title) => {
          try {
            await api.updateSession(sessionId, { title });
            sessionListOrchestrator.applyMutation((state) =>
              applySessionRenamed(state as ChatState, sessionId, title)
            );
          } catch (error) {
            set({ backgroundError: extractUserFacingMessage(error) });
          }
        },

        archiveSession: async (sessionId) => {
          try {
            const updated = await api.updateSessionNavigation(sessionId, { is_archived: true });
            if (updated.is_archived !== true) throw new Error("Archive not confirmed");
            sessionListOrchestrator.applyMutation((state) =>
              applySessionArchived(state as ChatState, sessionId)
            );
          } catch (error) {
            set({ backgroundError: extractUserFacingMessage(error) });
          }
        },

        unarchiveSession: async (sessionId) => {
          try {
            const updated = await api.updateSessionNavigation(sessionId, { is_archived: false });
            if (updated.is_archived !== false) throw new Error("Restore not confirmed");
            sessionListOrchestrator.applyMutation((state) =>
              applySessionUnarchived(state as ChatState, sessionId)
            );
          } catch (error) {
            set({ backgroundError: extractUserFacingMessage(error) });
          }
        },

        toggleShowArchived: () => set((state) => ({ showArchived: !state.showArchived })),

        shareSession: async (sessionId) => {
          const state = get();
          const session = state.sessions.find((s) => s.session_id === sessionId);
          const isCurrentSession = state.currentSession?.session_id === sessionId;
          const msgs = isCurrentSession
            ? state.messages
            : (state.messagesBySession[sessionId] ?? []);
          const title = session?.title || 'Conversation';
          const lines = [`# ${title}\n`];
          for (const m of msgs) {
            const role =
              m.role === 'user' ? '👤 User' : m.role === 'assistant' ? '🤖 Assistant' : '⚙️ System';
            lines.push(`## ${role}\n${m.content}\n`);
          }
          const text = lines.join('\n');
          try {
            await navigator.clipboard.writeText(text);
          } catch {
            // fallback: do nothing
          }
          return text;
        },

        clearMessages: () =>
          set((state) => {
            if (!state.currentSession) {
              return { messages: [], historicalMessageIds: [], currentThreadView: null };
            }
            return {
              messages: [],
              historicalMessageIds: [],
              currentThreadView: state.currentThreadView
                ? { ...createEmptyThreadView(state.currentThreadView.threadId), sessionId: state.currentSession.session_id }
                : null,
              messagesBySession: {
                ...state.messagesBySession,
                [state.currentSession.session_id]: [],
              },
            };
          }),

        toggleDeepThinking: (currentValue) => set({ deepThinking: !currentValue }),
        toggleWebSearch: () => set((state) => ({ webSearch: !state.webSearch })),
      };
    },
    {
      name: 'chat-storage',
      version: 2,
      migrate: (persisted, version) => {
        const old = persisted as Record<string, unknown>;
        if (version === 0) {
          if ('actorMode' in old && !('draftActorMode' in old)) {
            old.draftActorMode = old.actorMode;
            delete old.actorMode;
          }
        }
        if (version < 2) {
          delete old.messagesBySession;
        }
        return persisted;
      },
      partialize: (state) => buildChatStorePersistedSlice(state),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // CI-H1: drop the whole persisted slice (or the foreign parts of it)
        // when it does not belong to the currently authenticated user.
        const authUserId = readPersistedAuthUserId();
        const sanitized = sanitizeRehydratedStateForUser(state, authUserId);
        if (sanitized) {
          Object.assign(state, sanitized);
          return;
        }
        if (!state.currentSession) return;
        // CI-2: never rebuild a thread view for a session that does not
        // belong to the currently authenticated user (cross-user leak).
        if (!shouldRebuildThreadViewOnRehydrate(state.currentSession, authUserId)) {
          state.currentThreadView = null;
          return;
        }
        const threadId = state.currentSession.thread_id;
        if (threadId && !state.currentThreadView) {
          state.currentThreadView = {
            ...createEmptyThreadView(threadId),
            sessionId: state.currentSession.session_id,
            messages: [],
            historicalMessageIds: [],
            source: 'empty' as const,
            version: 0,
          };
        }
      },
    }
  )
);

/**
 * CI-2 helper: read the authenticated user id from the persisted auth store
 * without importing authStore (authStore already imports chatStore, so a
 * direct import would be circular). Fail-to-unknown: any parse error yields
 * null, which makes the rehydrate guard refuse to rebuild the view.
 */
function readPersistedAuthUserId(): string | null {
  try {
    const raw = window.localStorage.getItem('auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      state?: { user?: { id?: string } | null };
    };
    const userId = parsed?.state?.user?.id;
    return typeof userId === 'string' && userId ? userId : null;
  } catch {
    return null;
  }
}

/** Derive the active actor mode: session takes priority, draft is fallback. */
export function selectActiveActorMode(state: { currentSession: Session | null; draftActorMode: 'teacher' | 'student' }): 'teacher' | 'student' {
  return normalizeActorMode(state.currentSession?.actor_mode) ?? state.draftActorMode;
}

/**
 * Single derived authority for the currently active thread id.
 *
 * CD-3 thread-id convergence: the canonical id is *derived* from the thread
 * runtime view (falling back to the current session's thread id), NOT read from
 * the persisted top-level `currentThreadId` field. `currentThreadId` remains in
 * the persisted slice for migration safety but stops being a maintained read
 * authority — every read site resolves through this selector.
 */
export function selectCurrentThreadId(state: ThreadAuthoritySnapshot): string | null {
  return state.currentThreadView?.threadId ?? state.currentSession?.thread_id ?? null;
}

/** Public thread-authority selector: delegates to the single derived authority. */
export function selectCurrentThreadAuthority(state: ThreadAuthoritySnapshot): string | null {
  return selectCurrentThreadId(state);
}

/** Transcript authority for the currently active thread. */
export function selectCurrentTranscriptMessages(state: TranscriptAuthoritySnapshot): Message[] {
  return state.currentThreadView?.messages ?? state.messages;
}

/** Historical id authority aligned to the current transcript source. */
export function selectCurrentHistoricalMessageIds(state: TranscriptAuthoritySnapshot): string[] {
  return state.currentThreadView?.historicalMessageIds ?? state.historicalMessageIds;
}

/** Subscribe only to session list state. */
export const useSessionList = () =>
  useChatStore(
    useShallow((s) => ({
      sessions: s.sessions,
      currentSession: s.currentSession,
      isLoadingSessions: s.isLoadingSessions,
      sessionsTotal: s.sessionsTotal,
      showArchived: s.showArchived,
    }))
  );

/** Subscribe only to session-management actions. */
export const useSessionActions = () =>
  useChatStore(
    useShallow((s) => ({
      loadSessions: s.loadSessions,
      loadMoreSessions: s.loadMoreSessions,
      startDraftSession: s.startDraftSession,
      createSession: s.createSession,
      deleteSession: s.deleteSession,
      renameSession: s.renameSession,
      archiveSession: s.archiveSession,
      unarchiveSession: s.unarchiveSession,
      shareSession: s.shareSession,
      setCurrentSession: s.setCurrentSession,
      toggleShowArchived: s.toggleShowArchived,
    }))
  );
